import React, { useMemo, useState, useContext, useCallback, useRef, useEffect } from 'react';
import { useParams, useLocation } from 'react-router';
import { StaticContext } from '@sm/webassets';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { Accordion } from '@wds/accordion';
import { IconCaretUp, IconCaretDown } from '@wds/icons';
import { Button } from '@sm/wds-react';
import getFeatureCategories from '~pricing/transformers/details';
import FeatureTooltip from '~pricing/components/Tooltip';
import categoryGroupings, { categoryIds } from '~app/pages/Pricing/transformers/details/groupings';
import { PackageDataSet } from '~shared/types/pricing/package';
import { NavRouteParams } from '~pricing/components/Navigation/types';
import { usePackages } from '~shared/hooks/usePackages';
import { usePricingExperiments } from '~shared/hooks/usePricingExperiments';
import { formatCostInLocaleCurrency } from '~app/pages/Pricing/lib/lib-helpers';
import { DetailsFeatureRowFeature } from '~pricing/transformers/details/types';

import FeatureCell from './FeatureCell';
import useCategoryStyles from './useStyles';
import COPY from '~app/pages/Pricing/copy';
import { ToggleState } from './types';

const CaretDown = <IconCaretDown title="Down Arrow" />;
const CaretUp = <IconCaretUp title="Up Arrow" />;
const expandAll = t(COPY.EXPAND_ALL);
const collapseAll = t(COPY.COLLAPSE_ALL);

export default function Categories({
  displayedPackages,
  isMobile,
}: {
  displayedPackages: PackageDataSet;
  isMobile?: boolean;
}): JSX.Element {
  const {
    environment: { slLanguageLocale },
  } = useContext(StaticContext);
  const { canonicalPath, subPath } = useParams<NavRouteParams>();
  const { uiExperiment } = usePricingExperiments();

  const { overageCost } = usePackages();

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const featureId = query.get('selected_feature');
  const highlightedFeature = useRef<HTMLObjectElement>(null);
  const categoryId = query.get('selected_category');
  const selectedCategoryRef = useRef<HTMLObjectElement>(null);

  // Scroll to the category or highlighted feature if it exists on page load
  useEffect(() => {
    if ((highlightedFeature.current || selectedCategoryRef.current) && !isMobile) {
      setTimeout(() => {
        const offsetTop = highlightedFeature?.current?.offsetTop ?? selectedCategoryRef?.current?.offsetTop ?? 0;
        const packagesHeader = document.getElementById('PackagesHeader');
        const offsetHeight = packagesHeader ? packagesHeader.clientHeight : 0;

        window.scrollTo({ top: offsetTop - offsetHeight, behavior: 'smooth' });
      }, 1000);
    }
  }, [isMobile]);

  const {
    AccordionControl,
    DetailRow,
    Feature,
    HighlightFeature,
    TooltipContainer,
    TooltipIndicator,
    Tooltip,
    ExpandCollapse,
  } = useCategoryStyles({
    packageCount: displayedPackages.length,
  });

  const formattedOverageCost = useMemo(
    () => overageCost && formatCostInLocaleCurrency(overageCost, 2),
    [overageCost]
  ) as string;

  // get the experience name if there is one
  const experience = (uiExperiment.hasAssignment && uiExperiment.treatmentName) || '';
  /**
   * Since /pricing/details is a valid route lets handle experiments categories accordingly
   * See src/app/pages/Pricing/pages/index.ts L24 for the regex path matching
   */
  const currentPath = canonicalPath ?? subPath;
  const categories = useMemo(() => {
    const groupings = categoryGroupings[currentPath][experience] ?? categoryGroupings[currentPath].default;
    return getFeatureCategories(
      displayedPackages,
      groupings,
      slLanguageLocale,
      formattedOverageCost,
      experience,
      currentPath
    );
  }, [experience, currentPath, displayedPackages, slLanguageLocale, formattedOverageCost]);
  const categoryIndices: number[] = categories.map((_category, index) => index);

  const initialState: ToggleState = useMemo(() => {
    return {
      initialLoad: isMobile,
      isExpanded: !isMobile,
      indices: isMobile ? [0] : categoryIndices,
      text: isMobile ? expandAll : collapseAll,
      icon: isMobile ? CaretUp : CaretDown,
    };
  }, [categoryIndices, isMobile]);
  const [toggleData, setToggleData] = useState(initialState);

  const handleToggle = useCallback(
    (event: React.SyntheticEvent): void => {
      event.preventDefault();
      setToggleData({
        initialLoad: false,
        isExpanded: !toggleData.isExpanded,
        indices: !toggleData.initialLoad && toggleData.indices.length ? [] : categoryIndices,
        text: toggleData.isExpanded ? expandAll : collapseAll,
        icon: toggleData.isExpanded ? CaretUp : CaretDown,
      });
    },
    [categoryIndices, toggleData]
  );

  const key = isMobile ? 'Mobile' : 'Desktop';

  const featureRowCells = (feature: DetailsFeatureRowFeature, id: string): JSX.Element => {
    return (
      <>
        {feature?.packages.map(pkg => (
          <FeatureCell {...pkg} key={`${key}-${id}-${pkg.packageName}`} />
        ))}
      </>
    );
  };

  return (
    <>
      <Button size="sm" className={ExpandCollapse} onClick={handleToggle}>
        {toggleData.text}
        {toggleData.icon}
      </Button>
      <Box className={AccordionControl} data-testid="Categories">
        <Accordion
          multiple
          onChange={(_expandedIndices: React.ReactNode[], index: number, _id: string, isExpanding: boolean) => {
            setToggleData(state => {
              return {
                ...state,
                indices: isExpanding ? [...state.indices, index] : state.indices.filter(item => item !== index),
              };
            });
          }}
        >
          {/* name: NAME rename to preserve the COPY constant format */}
          {categories.map(({ name: NAME, features }, index) => {
            const isSelectedCategory =
              categoryId && categoryIds[categoryId as keyof typeof categoryIds] === NAME.defaultMessage;
            return (
              <Accordion.Item
                key={`${key}-${NAME.id}`}
                id={t(NAME)}
                expanded={
                  !!toggleData.indices.includes(index) ||
                  Boolean(featureId && features.find(feature => feature.name === featureId))
                }
                title={t(NAME)}
                ref={isSelectedCategory ? selectedCategoryRef : null}
              >
                <section role="table" aria-label={NAME.defaultMessage}>
                  {features?.map(feature => {
                    const isHighlightedFeature = featureId && featureId === feature.name;
                    return (
                      <div
                        className={`${DetailRow} ${isHighlightedFeature ? HighlightFeature : ''}`}
                        ref={isHighlightedFeature ? highlightedFeature : null}
                        key={`${key}-${feature.name}`}
                        role="row"
                        tabIndex={-1}
                      >
                        <div className={Feature} role="rowheader" aria-label={feature.summary} tabIndex={-1}>
                          {feature.summary}
                          {feature.tooltip && feature.tooltipHeading && (
                            <span className={TooltipContainer}>
                              <span className={TooltipIndicator}>?</span>
                              <span className={Tooltip} role="tooltip">
                                <FeatureTooltip tooltip={feature.tooltip} tooltipHeading={feature.tooltipHeading} />
                              </span>
                            </span>
                          )}
                        </div>
                        {featureRowCells(feature, NAME.id)}
                      </div>
                    );
                  })}
                </section>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Box>
    </>
  );
}
